import { trackHeapEvent } from './heap/index';
import * as monitoring from './monitoring';
import type { InitializeParams } from './monitoring/types/types';

let trackingData: { userId?: string } = {};

/**
 * @param {Object} newData
 * @returns {Void}
 */
const setTrackingData = (newData: Record<string, unknown>): void => {
  trackingData = {...trackingData, ...newData};
};

/**
 * Initializes tracking and verifies necessary information is present
 * @param {String} userId id of the current user
 * @returns {Void}
 */
export function initializeSegment (userId: string): void {
  if (!userId) {
    throw new Error('Missing user id');
  }
  setTrackingData({ userId });
}

/**
 * Initializes the heap analytics
 * @param {Object} data user details, subscriptions, feature flags
 */
export function initializeAnalytics(data: InitializeParams['trackingData']): void {
  monitoring.initialize({trackingData: data})
}

/**
 * Initializes the heap analytics
 * @param {string} eventName
 * @param {object} eventProperties
 */
export function trackCustomHeapEvent(eventName: string, eventProperties: object): void {
  trackHeapEvent(eventName, eventProperties);
}

/**
 * STUBBED as tracker (our old in-house analytics tool) is shut down, 
 * apps are still firing events but these events won't go anywhere it 
 * was decided not to send these somewhere else 
 * 
 * Perform the request
 * @param {String} method the tracking method being used (track, identify, page)
 * @param {Object} customData Optional params to be sent with call
 * @param {Function?} callback Optional callback method when http call is done.
 *    Will be called as such: callback(error) (where error is null on success)
 * @returns {Promise}
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function analyticsRequest(_method: string, _customData: object, _callback?: (error: Error | null) => void): Promise<void> {
  return Promise.resolve();
}

/**
 * Ties user info (e.g. firstname, lastname, email, etc) to a particular
 * user id
 * @param {Object} traits user traits to be sent with call
 * @param {Function?} callback Optional callback method when http call is done.
 *    Will be called as such: callback(error) (where error is null on success)
 * @returns {Promise}
 */
export function identifyUser(traits: object, callback?: (error: Error | null) => void): Promise<void> {
  return analyticsRequest('identify', traits, callback);
}

/**
 * Tracks user actions
 * @param {string} event the name of the event being tracked
 * @param {Object} properties event properties
 * @param {Object} context event context
 * @param {Function?} callback Optional callback method when http call is done.
 *    Will be called as such: callback(error) (where error is null on success)
 * @returns {Promise}
 */
export function trackAction(event: string, properties: object, context: object, callback?: (error: Error | null) => void): Promise<void> {
  const data = {
    event,
    properties,
    context
  };
  return analyticsRequest('track', data, callback);
}

/**
 * Tracks page views
 * @param {string} pageName the name of the page being tracked
 * @param {Object} data Optional any additional page properties
 * @param {Function?} callback Optional callback function
 * @returns {Promise}
 */
export function trackPageView(pageName: string, data: object, callback?: (error: Error | null) => void): Promise<void> {
  return analyticsRequest('page', { ...data, name: pageName }, callback);
}

/**
 * STUBBED as tracker (our old in-house analytics tool) is shut down, 
 * apps are still firing events but these events won't go anywhere it 
 * was decided not to send these somewhere else 
 * 
 * Send multiple tracking events as a batch request.
 * @param {Array.<Object>} events Array of event objects: { event, properties, context }
 * @param {Function?} callback Optional callback function
 * @returns {Promise}
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function batchTrackAction(_events: object[], _callback?: (error: Error | null) => void): Promise<void> {
  return Promise.resolve();
}

// Required for tests
// eslint-disable-next-line no-underscore-dangle
export const __resetTrackingData = () => {
  trackingData = {};
};
// Required for tests
// eslint-disable-next-line no-underscore-dangle
export const __getTrackingData = () => trackingData;

export { monitoring };

export { getDatadogRumInstance, initializeRum, type RumClient } from './datadog';

export * as DD from './datadog';

export { addConsentListener, getConsentState, removeConsentListenerById } from './oneTrust/oneTrust';
